import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Full-screen intro shown the moment a visitor lands. It preloads the story
// frame sequence while offering a language choice (English / हिन्दी). Once a
// language is chosen AND loading reaches 100%, it auto-dismisses and the
// experience begins — the tap doubles as the user gesture that unlocks audio.
export const IntroLoader = ({
  onEnter,
  urls = null,
  total = 128,
  basePath = "/images/janmashtami",
  prefix = "ezgif-frame-",
  pad = 3,
}) => {
  const [loaded, setLoaded] = useState(0);
  const [chosen, setChosen] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const firedRef = useRef(false);

  const frameUrls =
    urls && urls.length
      ? urls
      : Array.from(
          { length: total },
          (_, i) => `${basePath}/${prefix}${String(i + 1).padStart(pad, "0")}.jpg`
        );
  const count = frameUrls.length;
  const pct = Math.round((loaded / count) * 100);
  const ready = loaded >= count;

  useEffect(() => {
    let mounted = true;
    let done = 0;
    frameUrls.forEach((src) => {
      const img = new Image();
      img.src = src;
      img.onload = img.onerror = () => {
        if (!mounted) return;
        done += 1;
        setLoaded(done);
      };
    });
    // Safety: never trap the visitor if a frame stalls.
    const fallback = setTimeout(() => mounted && setLoaded(count), 12000);
    return () => {
      mounted = false;
      clearTimeout(fallback);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Auto-start the moment both conditions are met.
  useEffect(() => {
    if (chosen && ready && !firedRef.current) {
      firedRef.current = true;
      setLeaving(true);
      const id = setTimeout(() => onEnter(chosen), 700);
      return () => clearTimeout(id);
    }
  }, [chosen, ready, onEnter]);

  const LANGS = [
    { code: "en", label: "English", sub: "Enter in English" },
    { code: "hi", label: "हिन्दी", sub: "हिंदी में देखें" },
  ];

  return (
    <AnimatePresence>
      {!leaving && (
        <motion.div
          data-testid="intro-loader"
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden bg-[#070b18] px-6 text-center"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7, ease: "easeInOut" }}
        >
          {/* soft radial glow */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(60% 55% at 50% 40%, rgba(245,180,60,0.14), rgba(7,11,24,0) 70%)",
            }}
          />

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative z-10 flex flex-col items-center"
          >
            <span className="mb-4 text-3xl">🪈</span>
            <p className="font-display text-5xl text-amber-300 sm:text-6xl">
              श्री कृष्ण
            </p>
            <p className="mt-3 text-xs uppercase tracking-[0.4em] text-amber-100/50 sm:text-sm">
              Take Krishna Home
            </p>

            {/* language choice */}
            <div
              data-testid="intro-language-choice"
              className="mt-10 flex flex-col items-center gap-4 sm:flex-row"
            >
              {LANGS.map((l) => {
                const active = chosen === l.code;
                return (
                  <button
                    key={l.code}
                    data-testid={`intro-lang-${l.code}`}
                    onClick={() => setChosen(l.code)}
                    className={`group flex min-w-[168px] flex-col items-center rounded-2xl border px-8 py-4 transition-all duration-300 ${
                      active
                        ? "border-amber-300 bg-amber-400/15 shadow-[0_0_30px_-8px_rgba(245,180,60,0.6)]"
                        : "border-amber-300/25 bg-white/[0.02] hover:border-amber-300/60 hover:bg-amber-400/10"
                    }`}
                  >
                    <span
                      className={`font-display text-2xl ${
                        active ? "text-amber-200" : "text-amber-100/90"
                      }`}
                    >
                      {l.label}
                    </span>
                    <span className="mt-1 text-[11px] tracking-wider text-amber-100/40">
                      {l.sub}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* progress */}
            <div className="mt-10 h-[2px] w-56 overflow-hidden rounded-full bg-white/10">
              <div
                data-testid="intro-progress-bar"
                className="h-full bg-gradient-to-r from-amber-400 to-amber-200 transition-all duration-200"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p
              data-testid="intro-status"
              className="mt-3 h-4 text-xs tracking-[0.3em] text-amber-100/40"
            >
              {chosen && !ready
                ? `Preparing your darshan… ${pct}%`
                : ready
                ? chosen
                  ? "Entering…"
                  : "Choose a language to begin"
                : `${pct}%`}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
